/**
 * useAbnLookup Hook
 *
 * Encapsulates all ABN/ACN input handling, formatting, validation,
 * and API lookup logic extracted from PartyDetails.
 *
 * Responsibilities:
 * - Auto-format ABN (11-digit) / ACN (9-digit) input
 * - Immediate checksum validation on full-length entry
 * - Trigger lookup via ABR/KYB API
 * - Expose lookup result, loading state, and error
 */

import { useState, useCallback } from 'react';
import { validateABN, validateACN, formatABN, formatACN } from '../utils/abnAcnValidator';
import { lookupABN, lookupACN, ABNLookupResult, ACNLookupResult } from '../services/abnAcnLookupService';

// Re-export for consumers
export type { ABNLookupResult, ACNLookupResult };

export type LookupResult = (ABNLookupResult | ACNLookupResult) & { abn?: string; acn?: string };

export type InputType = 'abn' | 'acn' | 'unknown';

/** Map ABR entity type codes to human-readable names */
const ENTITY_TYPE_MAP: Record<string, string> = {
  'PRV': 'Australian Private Company',
  'Private': 'Australian Private Company',
  'Australian Private Company': 'Australian Private Company',
  'PUB': 'Australian Public Company',
  'Public': 'Australian Public Company',
  'PUBLIC': 'Australian Public Company',
  'Australian Public Company': 'Australian Public Company',
  'GOV': 'Australian Government Body',
  'Government': 'Australian Government Body',
  'State Government Entity': 'State Government Entity',
  'Local Government Entity': 'Local Government Entity',
  'Commonwealth Government Entity': 'Commonwealth Government Entity',
  'TRT': 'Trust',
  'Trust': 'Trust',
  'DTT': 'Discretionary Trading Trust',
  'DIT': 'Discretionary Investment Trust',
  'Discretionary Trust': 'Discretionary Trust',
  'Fixed Trust': 'Fixed Trust',
  'Hybrid Trust': 'Hybrid Trust',
  'Unit Trust': 'Unit Trust',
  'Family Trust': 'Family Trust',
  'SMSF': 'Self-Managed Superannuation Fund',
  'PTR': 'Partnership',
  'Partnership': 'Partnership',
  'Limited Partnership': 'Limited Partnership',
  'IND': 'Sole Trader / Individual',
  'Individual/Sole Trader': 'Sole Trader / Individual',
  'Sole Trader': 'Sole Trader / Individual',
  'OTH': 'Other Entity',
  'Other': 'Other Entity',
  'Other Incorporated Entity': 'Other Incorporated Entity',
  'Other Unincorporated Entity': 'Other Unincorporated Entity',
  'Cooperative': 'Cooperative',
  'Association': 'Association',
  'Strata Title': 'Strata Title Body Corporate',
  'APTY|LMSH|PROP': 'Australian Proprietary Company',
  'APTY': 'Australian Proprietary Company',
  'LMSH': 'Limited by Shares',
  'PROP': 'Proprietary Company',
};

/** Resolve entity type code to display name */
export const resolveEntityType = (code: string | undefined): string => {
  if (!code) return '';
  return ENTITY_TYPE_MAP[code] || code;
};

export interface UseAbnLookupOptions {
  /** Initial ABN/ACN value (e.g. from saved form data) */
  initialValue?: string;
}

export interface UseAbnLookupReturn {
  /** The formatted ABN/ACN input string */
  abnAcnInput: string;
  /** Raw digits extracted from input */
  digits: string;
  /** Detected input type based on digit count */
  inputType: InputType;
  /** Dynamic label (changes after lookup confirms type) */
  inputLabel: string;
  /** Lookup result after successful API call */
  lookupResult: LookupResult | null;
  /** Whether a lookup is in progress */
  isLookingUp: boolean;
  /** Validation / lookup error message */
  error: string;
  /** Whether the entity has a "REMOVED" status */
  isRemoved: boolean;
  /** Handle change events on the input field */
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handle keydown (triggers lookup on Enter) */
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Trigger the ABN/ACN lookup manually */
  triggerLookup: () => Promise<void>;
  /** Clear the lookup result and reset input */
  reset: () => void;
  /** Set the input value programmatically */
  setInput: (value: string) => void;
}

/** Returns 'abn' | 'acn' | 'unknown' based on digit count */
export const detectInputType = (raw: string): InputType => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11) return 'abn';
  if (digits.length === 9) return 'acn';
  return 'unknown';
};

const cleanDigits = (v: string) => v.replace(/\D/g, '');

export function useAbnLookup(options: UseAbnLookupOptions = {}): UseAbnLookupReturn {
  const [abnAcnInput, setAbnAcnInput] = useState(options.initialValue || '');
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [error, setError] = useState('');

  const digits = cleanDigits(abnAcnInput);
  const inputType = detectInputType(digits);

  // Dynamic label — only show specific type after lookup confirms it
  const inputLabel =
    lookupResult && inputType === 'acn'
      ? 'ACN (Australian Company Number)'
      : lookupResult && inputType === 'abn'
        ? 'ABN (Australian Business Number)'
        : 'ABN or ACN';

  const isRemoved = !!(lookupResult && 'status' in lookupResult && lookupResult.status === 'REMOVED');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const d = cleanDigits(e.target.value);
    if (d.length > 11) return;

    // Auto-format
    const formatted = d.length <= 9 ? formatACN(d) : formatABN(d);
    setAbnAcnInput(formatted);
    setLookupResult(null);
    setError('');

    // Immediate checksum when full length entered
    if (d.length === 11) {
      const v = validateABN(d);
      if (!v.isValid) setError(v.message);
    } else if (d.length === 9) {
      const v = validateACN(d);
      if (!v.isValid) setError(v.message);
    }
  }, []);

  const triggerLookup = useCallback(async () => {
    const d = cleanDigits(abnAcnInput);
    const type = detectInputType(d);

    if (type === 'unknown') {
      setError('Enter exactly 11 digits (ABN) or 9 digits (ACN)');
      return;
    }

    // Validate checksum
    if (type === 'abn') {
      const v = validateABN(d);
      if (!v.isValid) { setError(v.message); return; }
    } else {
      const v = validateACN(d);
      if (!v.isValid) { setError(v.message); return; }
    }

    setError('');
    setIsLookingUp(true);

    try {
      let result: LookupResult | null = null;
      if (type === 'abn') {
        result = await lookupABN(d);
      } else {
        const acnResult = await lookupACN(d);
        if (acnResult) result = acnResult as LookupResult;
      }

      if (result) {
        setLookupResult(result);
      } else {
        setError(`${type.toUpperCase()} not found in the business register`);
      }
    } catch {
      setError('Lookup failed — please try again');
    } finally {
      setIsLookingUp(false);
    }
  }, [abnAcnInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      triggerLookup();
    }
  }, [triggerLookup]);

  const reset = useCallback(() => {
    setAbnAcnInput('');
    setLookupResult(null);
    setError('');
    setIsLookingUp(false);
  }, []);

  const setInput = useCallback((value: string) => {
    setAbnAcnInput(value);
    setLookupResult(null);
    setError('');
  }, []);

  return {
    abnAcnInput,
    digits,
    inputType,
    inputLabel,
    lookupResult,
    isLookingUp,
    error,
    isRemoved,
    handleChange,
    handleKeyDown,
    triggerLookup,
    reset,
    setInput,
  };
}
